import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit'
import { AquaAkuttidIds, OnlineDentistKeys } from '../../models/enums'
import { Clinic, Treatment, TreatmentCategory } from '../../models/types'
import { AppDispatch, RootState } from '../../store'
import { DateTime } from 'luxon'
import { findAvailableTreatmentIdsPerClinic, Screens } from 'patient-api-lib'
import { getTreatmentIdsFromCategories } from './treatmentUtils'
import { setNearestAppointmentDate } from '../appointmentScreen/appointmentScreenSlice'
import {
  fetchClinics,
  fetchClinicsWithoutCity,
  setClinics,
  setSelectedClinic,
} from '../clinicScreen/clinicScreenSlice'
import { sendSelectedTreatment } from '../../util/messageHandler'

interface TreatmentScreenState {
  treatments: TreatmentCategory[]
  selectedTreatment: Treatment[] | Treatment | null
  loading: boolean
}

export const initialState: TreatmentScreenState = {
  treatments: [],
  selectedTreatment: null,
  loading: true,
}

export const filterTreatments = createAsyncThunk<
  TreatmentCategory[],
  void,
  { dispatch: AppDispatch; state: RootState }
>('treatmentScreen/filterTreatments', async (_, thunkApi) => {
  const { city } = thunkApi.getState().cityScreen
  const {
    clinics,
    treatmentCategories: treatmentCategoriesFromConfig,
    treatmentIds: treatmentIdsFilter,
  } = thunkApi.getState().app
  const startDate = DateTime.local()
  const endDate = startDate.plus({ month: 3 })
  const cityName = city?.name

  if (!cityName) {
    throw new Error('City is not set')
  }

  const cityClinics = clinics.filter((c) => c.city === cityName)
  const clinicIds = cityClinics.map((c) => c.id)

  if (
    clinics.find((cl) => cl.id === OnlineDentistKeys.id) &&
    Array.isArray(clinicIds)
  ) {
    clinicIds.push(OnlineDentistKeys.id)
  }

  const clinicAvailabilityList = await findAvailableTreatmentIdsPerClinic(
    clinicIds,
    startDate.toJSDate(),
    endDate.toJSDate()
  )

  const filteredTreatmentIds = getTreatmentIdsFromCategories(
    treatmentCategoriesFromConfig,
    treatmentIdsFilter
  )

  const availableUpcomingTreatments = [
    ...new Set(
      clinicAvailabilityList
        .getClinicsList()
        .flatMap((c) => c.getTreatmentIdsList())
        .filter((t) => {
          return filteredTreatmentIds.includes(t)
        })
    ),
  ]

  const availableTreatmentsCategories = treatmentCategoriesFromConfig.filter(
    (tc) =>
      tc.treatmentsList.some((tr) =>
        availableUpcomingTreatments.includes(tr.id)
      )
  )

  const filterCategoriesByAvailableTreatments =
    availableTreatmentsCategories.map((t) => {
      const treatmentsList = t.treatmentsList.filter((tl) =>
        availableUpcomingTreatments.includes(tl.id)
      )

      return { ...t, treatmentsList }
    })

  const flatTreatmentToCategories: TreatmentCategory[] =
    filterCategoriesByAvailableTreatments.map((tc) => {
      if (tc.treatmentsList.length === 1) {
        const singleTreatment = tc.treatmentsList[0]

        return {
          name: singleTreatment.name || '',
          description: singleTreatment.description || '',
          extendedDescription: singleTreatment.extendedDescription,
          treatmentsList: [singleTreatment],
        }
      }

      return tc
    })

  return flatTreatmentToCategories
})

export const done = createAsyncThunk<
  Screens,
  void,
  { dispatch: AppDispatch; state: RootState }
>('treatmentScreen/done', async (_, thunkApi) => {
  const state = thunkApi.getState()
  const currentScreen = state.screensRouter.currentScreen
  const selectedTreatment = state.treatmentScreen.selectedTreatment
  const selectedTreatmentCategory =
    state.treatmentSelection.selectedTreatmentCategory
  const selectedClinic = state.clinicScreen.selectedClinic
  const city = state.cityScreen.city
  const treatmentIdsSelectedCategory =
    selectedTreatmentCategory?.treatmentsList.flatMap((t) => t.id)
  const isAquaAkuttid =
    treatmentIdsSelectedCategory?.includes(AquaAkuttidIds.Akuttid) &&
    treatmentIdsSelectedCategory?.includes(AquaAkuttidIds.AkuttidStandBy)

  if (
    !selectedTreatmentCategory ||
    (selectedTreatmentCategory.treatmentsList.length === 1 &&
      !selectedTreatment)
  ) {
    return Screens.TreatmentScreen
  }

  // SPECIFIC CASE: If the selected treatment is Aqua Akuttid, we need to set the all treatment from Akuttid group
  if (isAquaAkuttid) {
    const aquaAkuttidTreatment = selectedTreatmentCategory.treatmentsList
    sendSelectedTreatment(aquaAkuttidTreatment[0])
    thunkApi.dispatch(setSelectedTreatment(aquaAkuttidTreatment))
  }

  if (
    selectedTreatmentCategory.treatmentsList.length > 1 &&
    currentScreen !== Screens.TreatmentSelectionScreen &&
    !isAquaAkuttid
  ) {
    return Screens.TreatmentSelectionScreen
  }

  const updateClinicList = async (): Promise<Clinic[]> => {
    thunkApi.dispatch(setLoading(true))
    if (!city) {
      return await thunkApi
        .dispatch(fetchClinicsWithoutCity())
        .then((result) => {
          return result.payload as Clinic[]
        })
    } else {
      return await thunkApi.dispatch(fetchClinics()).then((result) => {
        return result.payload as Clinic[]
      })
    }
  }

  const updatedClinicList = await updateClinicList()
  thunkApi.dispatch(setClinics([])) // reset clinics in store as side-effect of fetchClinics()

  if (updatedClinicList.length === 1 || selectedClinic) {
    thunkApi.dispatch(setSelectedClinic(updatedClinicList[0] || selectedClinic))
    await thunkApi.dispatch(
      setNearestAppointmentDate(updatedClinicList[0] || selectedClinic)
    )

    thunkApi.dispatch(setLoading(true))
    return Screens.AppointmentScreen
  }

  return Screens.ClinicScreen
})

export const TreatmentScreenSlice = createSlice({
  name: 'treatmentScreen',
  initialState,
  reducers: {
    setSelectedTreatment: (
      state,
      action: PayloadAction<Treatment[] | Treatment | null>
    ) => {
      state.selectedTreatment = action.payload
    },
    setLoading: (state, action: PayloadAction<boolean>) => {
      state.loading = action.payload
    },
    clearState: () => initialState,
  },
  extraReducers: (builder) => {
    builder.addCase(filterTreatments.pending, (state) => {
      state.treatments = []
      state.loading = true
    })
    builder.addCase(filterTreatments.rejected, (state) => {
      state.treatments = []
      state.loading = false
    })
    builder.addCase(
      filterTreatments.fulfilled,
      (state, action: PayloadAction<TreatmentCategory[]>) => {
        state.treatments = action.payload
        state.loading = false
      }
    )
  },
})

export const { setSelectedTreatment, setLoading, clearState } =
  TreatmentScreenSlice.actions

export const selectTreatments = (state: RootState) => {
  return state.treatmentScreen.treatments
}

export const isFetchingTreatments = (state: RootState) =>
  state.treatmentScreen.loading

export const selectSelectedTreatment = (state: RootState) =>
  state.treatmentScreen.selectedTreatment

export const treatmentScreenReducer = TreatmentScreenSlice.reducer
